import React, { useMemo, useState } from 'react';
import { Button, Grid, InputAdornment, TextField, Tooltip } from '@material-ui/core';
import AddIcon from '@mui/icons-material/Add';
import { css } from '@emotion/react';
import { Tag } from '@onward-delivery/design/tag';
import { colors } from '@/styles';
import { Body1, SectionLabel } from '../blocks';
import SearchInput from '../../misc/SearchInput';
import { overlapMessage, partitionZipInput, zipOverlapIndex } from './utils';

// Capped so a big bad paste can't flood the Snackbar; past the cap it shows a count.
export const MAX_LISTED_ZIPS = 10;

const summarize = (zips) =>
    zips.length > MAX_LISTED_ZIPS
        ? `${zips.slice(0, MAX_LISTED_ZIPS).join(', ')} and ${zips.length - MAX_LISTED_ZIPS} more`
        : zips.join(', ');

// Duplicates aren't reported: they're already assigned.
const describeDropped = ({ invalid, unrecognized }) => {
    const parts = [];
    if (invalid.length)
        parts.push(`skipped ${invalid.length === 1 ? 'invalid zip' : 'invalid zips'} ${summarize(invalid)}`);
    if (unrecognized.length)
        parts.push(
            `skipped ${unrecognized.length === 1 ? 'unrecognized zip' : 'unrecognized zips'} ${summarize(unrecognized)}`
        );
    return parts.length ? parts.join('; ') : null;
};

const ZipSection = ({ zips, subregions, subregionId, callbacks, onAdd, onRemove }) => {
    const [input, setInput] = useState('');
    const [search, setSearch] = useState('');

    // Not keyed on `callbacks`: it is a new object every render.
    const overlapsByZip = useMemo(() => zipOverlapIndex(subregions, subregionId), [subregions, subregionId]);

    const visible = useMemo(() => {
        if (!search) return zips;
        return zips.filter((zip) => zip.includes(search));
    }, [zips, search]);

    const handleAdd = () => {
        const { added, invalid, unrecognized } = partitionZipInput(input, zips);

        if (added.length) onAdd(added);

        const dropped = describeDropped({ invalid, unrecognized });
        if (dropped) {
            callbacks.setNotification({
                severity: added.length ? 'info' : 'warning',
                message: added.length
                    ? `Added ${added.length} zip${added.length === 1 ? '' : 's'} — ${dropped}.`
                    : `Nothing to add — ${dropped}.`,
            });
        }

        setInput('');
    };

    return (
        <Grid
            container
            direction="column"
            css={css`
                flex-wrap: nowrap;
                border-top: 1px solid ${colors.greys.border};
                padding: 12px 16px 0;
            `}
        >
            <SectionLabel>{`Zip Codes · ${zips.length}`}</SectionLabel>
            <Body1
                css={css`
                    color: ${colors.greys[2]};
                    font-size: 12px;
                    line-height: 16px;
                    margin: 4px 0 12px;
                `}
            >
                Add zip codes by clicking them on the map, or type them below separated by commas.
            </Body1>

            <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Enter Zip Codes"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAdd();
                    }
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleAdd}
                                disabled={!input.trim()}
                                aria-label="add zips"
                                startIcon={<AddIcon fontSize="small" />}
                                css={css`
                                    height: 28px;
                                    min-width: 0;
                                    padding: 0 10px;
                                    text-transform: none;
                                    color: ${colors.greys[3]};
                                    border-color: ${colors.greys.border};
                                    .MuiButton-startIcon {
                                        margin-right: 2px;
                                    }
                                `}
                            >
                                Add
                            </Button>
                        </InputAdornment>
                    ),
                }}
            />

            {zips.length ? (
                <Grid
                    container
                    css={css`
                        margin-top: 12px;
                    `}
                >
                    <SearchInput placeholder="Search Zips" onChange={setSearch} />
                </Grid>
            ) : null}

            {/* Wrapper must stay bare: `.onward-design .flex` matches descendants only. */}
            <div className="onward-design">
                <div className="flex flex-wrap gap-1.5 py-3">
                    {visible.map((zip) => {
                        const others = overlapsByZip[zip];

                        const tag = (
                            <Tag
                                key={zip}
                                variant={others ? 'warning' : 'default'}
                                onRemove={() => onRemove(zip)}
                                onMouseEnter={() => callbacks.setZipHover?.(zip)}
                                onMouseLeave={() => callbacks.setZipHover?.(null)}
                            >
                                {zip}
                            </Tag>
                        );

                        // Tooltip needs a child that takes a ref; Tag doesn't.
                        return others ? (
                            <Tooltip key={zip} title={overlapMessage(others)} placement="top" arrow>
                                <span className="inline-flex">{tag}</span>
                            </Tooltip>
                        ) : (
                            tag
                        );
                    })}
                </div>
            </div>
            {zips.length && !visible.length ? (
                <Body1
                    css={css`
                        color: ${colors.greys[2]};
                        padding-bottom: 12px;
                    `}
                >
                    {`No zips matching "${search}"`}
                </Body1>
            ) : null}
        </Grid>
    );
};

export default ZipSection;
